import type { FunctionComponent } from "react";
import { useRef } from "react";
import { graphql, useFragment } from "react-relay";

import { useTimelineScrollEffect } from "~/components/article/article-render/live-article/hooks";
import { formatInTimeZone } from "~/lib/utils";
import type { itemLiveArticleTimelineLiveContent$key } from "~/queries/__generated__/itemLiveArticleTimelineLiveContent.graphql";

import { Container, Content, Dot, Left, Line, Time, Title } from "./styles";

type Props = {
  className?: string;
  id?: string;
  reference: itemLiveArticleTimelineLiveContent$key;
};

export const LiveArticleTimelineItem: FunctionComponent<Props> = ({ className, id, reference }) => {
  const containerReference = useRef<HTMLDivElement>(null);
  const { liveArticleScroller, setToScroll } = useTimelineScrollEffect();

  const content = useFragment(
    graphql`
      fragment itemLiveArticleTimelineLiveContent on LiveContent {
        entityId
        title
        customTime
      }
    `,
    reference,
  );

  const getIsHighlighted = (entityId: string | null) => {
    if (!entityId) return false;
    if (liveArticleScroller.highlightedEntityId)
      return liveArticleScroller.highlightedEntityId === entityId;
    return liveArticleScroller.toScrollEntityId === entityId;
  };

  return (
    <Container
      className={className}
      id={id}
      ref={containerReference}
      onClick={() => {
        if (!content?.entityId) return;
        setToScroll(content.entityId, "content", content.entityId);
      }}
    >
      <Left>
        <Dot />
        <Line />
      </Left>
      <Content>
        {content?.customTime && <Time>{formatInTimeZone(content.customTime, "h:mma")}</Time>}
        <Title $isHighlighted={getIsHighlighted(content.entityId)}>{content.title}</Title>
      </Content>
    </Container>
  );
};
LiveArticleTimelineItem.displayName = "LiveArticleTimelineItem";
